import { ControlMenuItemType } from '../control-menu';
import {
  ActionMenuItem,
  CheckboxMenuItem,
  RadioGroupMenuItem,
} from '../control-menu/types';

export const isActionReplayable = (item: ActionMenuItem): boolean =>
  item.replayable !== false;

export const getDefaultItemId = (
  items: ControlMenuItemType[]
): string | null => {
  for (const item of items) {
    if (item.type === 'radiogroup' && !item.disabled) {
      const firstOption = item.options[0];
      if (firstOption) {
        return firstOption.id;
      }
    }
    if (item.type === 'action' && !item.disabled && isActionReplayable(item)) {
      return item.id;
    }
    if (item.type === 'checkbox' && !item.disabled) {
      return item.id;
    }
  }
  return null;
};

export const getActiveCheckboxIds = (items: ControlMenuItemType[]): string[] =>
  items
    .filter(
      (item): item is CheckboxMenuItem =>
        item.type === 'checkbox' && (item.value() ?? false)
    )
    .map((item) => item.id);

export const findRadioGroupForOptionId = (
  items: ControlMenuItemType[],
  optionId: string
  // eslint-disable-next-line sonarjs/cognitive-complexity -- SonarJS trial (NUMARK-1243): complexity 16 > 15 allowed; flagged for later refactor, not fixed here
): RadioGroupMenuItem | undefined => {
  for (const item of items) {
    if (
      item.type === 'radiogroup' &&
      item.options.some((option) => option.id === optionId)
    ) {
      return item;
    }
    if (item.type === 'action' && item.children) {
      const nested = findRadioGroupForOptionId(item.children, optionId);
      if (nested) {
        return nested;
      }
    }
    if (item.type === 'checkbox' && item.children) {
      const nested = findRadioGroupForOptionId(item.children, optionId);
      if (nested) {
        return nested;
      }
    }
  }
  return undefined;
};

export const findCheckboxItemById = (
  items: ControlMenuItemType[],
  itemId: string
  // eslint-disable-next-line sonarjs/cognitive-complexity -- SonarJS trial (NUMARK-1243): complexity 16 > 15 allowed; flagged for later refactor, not fixed here
): CheckboxMenuItem | undefined => {
  for (const item of items) {
    if (item.type === 'checkbox' && item.id === itemId) {
      return item;
    }
    if (item.type === 'checkbox' && item.children) {
      const nested = findCheckboxItemById(item.children, itemId);
      if (nested) {
        return nested;
      }
    }
    if (item.type === 'action' && item.children) {
      const nested = findCheckboxItemById(item.children, itemId);
      if (nested) {
        return nested;
      }
    }
  }
  return undefined;
};

export const findActionItemById = (
  items: ControlMenuItemType[],
  itemId: string
  // eslint-disable-next-line sonarjs/cognitive-complexity -- SonarJS trial (NUMARK-1243): complexity 16 > 15 allowed; flagged for later refactor, not fixed here
): ActionMenuItem | undefined => {
  for (const item of items) {
    if (item.type === 'action' && item.id === itemId) {
      return item;
    }
    if (item.type === 'action' && item.children) {
      const nested = findActionItemById(item.children, itemId);
      if (nested) {
        return nested;
      }
    }
    if (item.type === 'checkbox' && item.children) {
      const nested = findActionItemById(item.children, itemId);
      if (nested) {
        return nested;
      }
    }
  }
  return undefined;
};

export const isReplayableItemId = (
  items: ControlMenuItemType[],
  itemId: string
): boolean => {
  if (findRadioGroupForOptionId(items, itemId)) {
    return true;
  }

  const actionItem = findActionItemById(items, itemId);
  if (actionItem) {
    return isActionReplayable(actionItem);
  }

  const checkboxItem = findCheckboxItemById(items, itemId);
  return checkboxItem ? !checkboxItem.disabled : false;
};

/** Keeps stored checkbox ids even when disabled so multi-select replay can restore the full snapshot. */
export const isStoredMultipleSelectionItemId = (
  items: ControlMenuItemType[],
  itemId: string
): boolean => {
  if (findCheckboxItemById(items, itemId)) {
    return true;
  }

  return isReplayableItemId(items, itemId);
};
